import React, { useState, SyntheticEvent } from 'react';

import { useTranslations } from '@lang';
import config from '@utils/config';
import { isEvent, isVoucher } from '@common-types/Guards';
import type { Item as EventItem } from '@common/components/ItemListingRows/Event/Event';
import type { Item as VoucherItem } from '@common/components/ItemListingRows/Voucher/Voucher';
import { BasketDetails } from 'shared-types/Basket';
import { addItemToBasket } from '@utils/basket/addToBasket';

import { DetailTitle, DetailsRow } from '../styled';
import { Body, Content, Edit, MessageRow } from './PersonalisationMessage.styles';
import { FulfilableBasketItemType } from '@features/AddedToBasket/Fulfilable/FulfilableAddedToBasket.types';
import {
  Personalization,
  PersonalizationFormValues,
} from '@features/AddedToBasket/Fulfilable/PersonaliseItem/types';
import { PersonalisationModal } from '@features/AddedToBasket/Fulfilable/PersonaliseItem/PersonalisationModal';
import { PersonalisationForm } from '@features/AddedToBasket/Fulfilable/PersonaliseItem/PersonalisationForm/PersonalisationForm';
import { AddFulfilableToBasketFormValues } from '@features/AddedToBasket/Fulfilable/AddFulfilableToBasketForm/AddFulfilableToBasketForm.types';
import { Property } from 'shared-types/Property';

type PersonalisationMessageProps = {
  property: Property;
  basketDetails: BasketDetails;
  hasDefaultEVoucher: boolean;
  hasDefaultOverprint: boolean;
  hasDefaultETicket: boolean;
  hasDefaultTicketOverprint: boolean;
  createBasket: Parameters<typeof addItemToBasket>[0]['createBasket'];
  updateBasket: Parameters<typeof addItemToBasket>[0]['updateBasket'];
  type: FulfilableBasketItemType;
  item: VoucherItem | EventItem;
  personalization: Personalization;
  isEditable?: boolean;
};

export function PersonalisationMessage({
  property,
  createBasket,
  updateBasket,
  basketDetails,
  hasDefaultEVoucher,
  hasDefaultOverprint,
  hasDefaultETicket,
  hasDefaultTicketOverprint,
  type,
  item,
  personalization,
  isEditable = true,
}: PersonalisationMessageProps) {
  const [showModal, setShowModal] = useState<boolean>(false);
  const { t } = useTranslations();

  const name = isVoucher(item) ? item.voucher.name : item.event.name;

  const previewOptions = {
    propertyId: property?.id,
    hasDefaultEVoucher,
    hasDefaultOverprint,
    hasDefaultETicket,
    hasDefaultTicketOverprint,
  };

  function openModal(event: SyntheticEvent) {
    event.preventDefault();
    setShowModal(true);
  }

  function hideModal() {
    setShowModal(false);
  }

  function updatePersonalisation(
    itemId: number,
    personalization: PersonalizationFormValues['personalization']
  ) {
    const item = basketDetails.items.find((x) => x.id === itemId);

    if (!item) {
      throw new Error('No item found');
    }

    if (!isVoucher(item) && !isEvent(item)) {
      throw new Error('Item is not a voucher or event');
    }

    const data: AddFulfilableToBasketFormValues = {
      amount: undefined, // @todo for monetary vouchers,
      option: item.option || (isVoucher(item) ? item.voucherOption : item.eventOption),
      quantity: 1,
      personalization,
      [type]: {
        id: isVoucher(item) ? item.voucherId : item.eventId,
      },
      type,
    };

    addItemToBasket({
      type: type == 'voucher' ? 'vouchers' : 'events',
      data,
      createBasket,
      updateBasket,
      property,
      basketDetails,
      language: config.lang,
      onSuccess: hideModal,
      replaceExistingItem: { id: item.id },
    });
  }

  return (
    <>
      <DetailsRow>
        <Content>
          <DetailTitle showOnMobile>
            {personalization ? t('common.personalisedMessage') : t('common.notPersonalised')}
          </DetailTitle>
          {personalization && (
            <Body>
              {(personalization.salutation || personalization.recipient) && (
                <MessageRow>
                  {personalization.salutation} {personalization.recipient}
                </MessageRow>
              )}
              {personalization.messageLineOne && (
                <MessageRow>{personalization.messageLineOne}</MessageRow>
              )}
              {personalization.messageLineTwo && (
                <MessageRow>{personalization.messageLineTwo}</MessageRow>
              )}
              {personalization.messageLineThree && (
                <MessageRow>{personalization.messageLineThree}</MessageRow>
              )}
              {personalization.messageLineFour && (
                <MessageRow>{personalization.messageLineFour}</MessageRow>
              )}
              {personalization.messageLineFive && (
                <MessageRow>{personalization.messageLineFive}</MessageRow>
              )}
            </Body>
          )}

          {isEditable && (
            <div>
              <Edit href="#" onClick={openModal}>
                {t('personalization.edit')}
              </Edit>
            </div>
          )}
        </Content>
      </DetailsRow>
      <PersonalisationModal title={name} active={showModal} type={type} close={hideModal}>
        <PersonalisationForm
          item={item}
          previewOptions={previewOptions}
          initialValues={{
            personalization: [personalization],
          }}
          handleSubmission={updatePersonalisation}
        />
      </PersonalisationModal>
    </>
  );
}
